import type { ApiResponse } from '../types/api';
import type { User } from '../types/auth';
import type { Company } from '../types/company';
import httpClient from './http/httpClient';

export interface PlatformSettings {
  maintenanceMode?: boolean;
  allowNewRegistrations?: boolean;
  maxUsersPerCompany?: number;
  maxTendersPerCompany?: number;
  enableMFA?: boolean;
  sessionTimeout?: number;
  enableMatchmakingFeature?: boolean;
}

export interface ClientResponse {
  success: boolean;
  clients?: Company[];
  client?: Company;
  message?: string;
  total?: number;
}

export interface TokenResponse {
  success: boolean;
  token?: string;
  tokenId?: string;
  expiresAt?: string;
  message?: string;
}

export const getPlatformSettings = async (): Promise<PlatformSettings> => {
  try {
    const response = await httpClient.post<{
      success: boolean;
      settings: PlatformSettings;
      message?: string;
    }>('/getPlatformSettings');

    if (response.success) {
      return response.settings;
    } else {
      throw new Error(response.message || 'Failed to fetch platform settings');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(
      `Fetch platform settings failed: ${err.response?.data?.message || err.message}`
    );
  }
};

export const updatePlatformSettings = async (
  settings: Partial<PlatformSettings>
): Promise<ApiResponse<PlatformSettings>> => {
  try {
    const response = await httpClient.post<ApiResponse<PlatformSettings>>(
      '/updatePlatformSettings',
      settings
    );
    return response;
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(
      `Failed to update platform settings: ${err.response?.data?.message || err.message}`
    );
  }
};

export const getUsersForClient = async (
  clientId: string,
  signal?: AbortSignal
): Promise<{ success: boolean; users: User[]; message?: string }> => {
  try {
    const response = await httpClient.post<{ success: boolean; users: User[]; message?: string }>(
      '/getUsersForClient',
      {
        clientId,
      },
      signal ? { signal } : undefined
    );

    if (response.success) {
      return response;
    } else {
      throw new Error(response.message || 'Failed to fetch users for client');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Fetch users for client failed: ${err.response?.data?.message || err.message}`);
  }
};

export const getClients = async (): Promise<ClientResponse> => {
  try {
    const response = await httpClient.post<ClientResponse>('/getClients');
    return response;
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Failed to fetch clients: ${err.response?.data?.message || err.message}`);
  }
};

export const updateClientProfile = async (
  clientId: string,
  profileData: Partial<Company> & { numTeams?: number }
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      '/updateClientProfile',
      { clientId, profileData }
    );
    return response;
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Failed to update client profile: ${err.message}`);
  }
};

export const allocateTeamLimits = async (
  teamId: string,
  limits: Record<string, number>
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      '/allocateTeamLimits',
      { teamId, limits }
    );
    return response;
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(
      `Failed to allocate team limits: ${err.response?.data?.message || err.message}`
    );
  }
};

import { doc, getDoc } from 'firebase/firestore';
import { firestore } from './firebase';

export const getClientData = async (clientId: string): Promise<Company | null> => {
  const snap = await getDoc(doc(firestore, 'clients', clientId));
  if (snap.exists()) {
    return { ...snap.data(), id: snap.id } as Company;
  }
  return null;
};
